import { TAX_INVOICE_THRESHOLD_MINOR } from "@stb/jurisdiction-au"
import {
  Briefcase,
  Camera,
  Copy,
  Inbox,
  Percent,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Store,
  Wrench,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { ReceiptIllustration } from "../../../components/home/home-art"
import { ListMock, ScanHeroMock } from "../../../components/landing/product-art"
import { type ProductPageContent, withIcons } from "../../../components/landing/product-page"
import { RECEIPT_EXAMPLE as ex } from "../../products/example-numbers"
import { RECEIPT_CHECKED, RECEIPT_SCANNER } from "../../products/receipt-scanner"
import { englishDateToZh, STORAGE_SENTENCE_ZH } from "../dates"

/** Chinese counterpart of products/receipt-scanner.tsx; terms: docs/design/landing/copy-zh.md. */
const ATO_TAX_INVOICES =
  "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices"
const CHECKED = englishDateToZh(RECEIPT_CHECKED)
const threshold = `A$${(TAX_INVOICE_THRESHOLD_MINOR / 100).toFixed(2)}`

export const RECEIPT_SEO_ZH = {
  title: "带 GST 核对的收据扫描应用",
  description:
    "拍下收据或上传 PDF，我们读出商家、日期、总额和 GST，由你确认后才入账。有免费版，适合澳大利亚个体经营者。",
}

const COPY: ProductPageContent = {
  path: "/receipt-scanner",
  name: "收据扫描",
  tag: "收据和开支",
  reviewed: englishDateToZh(RECEIPT_SCANNER.reviewed),
  h1: "帮你核对 GST 的收据扫描应用",
  lead: "拍下收据或上传 PDF，我们读出商家、日期、总额和 GST。每一张由你确认后，才会成为开支。",
  answer: {
    text: `就 GST 而言，购买金额超过 ${threshold}（含 GST，2026–27 财年）时，一般需要有 Tax Invoice 才能申请进项抵扣。我们会读取每张收据，标出超过该金额却缺少发票信息的采购，并保留原始文件。`,
    sources: [{ label: "ATO，Tax Invoice", href: ATO_TAX_INVOICES }],
    checked: CHECKED,
  },
  cta: { label: "开始免费试用", href: "/login" },
  secondaryCta: { label: "查看价格", href: "/pricing" },
  heroNote: "AI 可能读错收据，所以你确认之前，什么都不会入账。",
  stats: [
    { value: threshold, label: "Tax Invoice 核对线" },
    { value: "0 到 100%", label: "业务用途占比" },
    { value: "待复核清单", label: "存放看不清的收据" },
    { value: "原件", label: "保留供你查看" },
  ],
  stepsTitle: "拍照进来，核对后的开支出去",
  steps: [
    { title: "拍摄", body: "拍一张照片，或把 PDF 拖到网站上。" },
    {
      title: "复核",
      body: `看不清楚的情况会留在复核清单里，例如超过 ${threshold} 却没有 ABN，或 GST 和总额对不上。`,
    },
    { title: "确认", body: "设置业务用途占比并确认。只有你确认后，这笔开支才会入账。" },
  ],
  example: {
    title: "两张示例收据",
    caption: "两张示例收据",
    columns: ["示例收据", "总额", "GST"],
    rows: [
      { header: ex.merchant1, cells: [ex.total1, ex.gst1] },
      { header: ex.merchant2, cells: [ex.total2, ex.gst2] },
    ],
    note: "仅为示例。GST 是含 GST 总额的十一分之一。每张收据都留在复核清单里，等你确认。仅为估算。",
  },
  included: [
    {
      title: "自动读取的字段",
      body: "商家、ABN、日期、总额和 GST。有字段没读清楚的收据会被标出，请你检查。",
    },
    { title: "复核清单，而不是猜测", body: "把握不大或有异常的收据会等你处理，不会悄悄入账。" },
    {
      title: "GST 进项核对",
      body: `购买金额超过 ${threshold} 却缺少 Tax Invoice 信息时，会给出提示。`,
    },
    { title: "业务用途占比", body: "每笔开支可设 0 到 100%。" },
    { title: "重复提醒", body: "同一个文件，或 ABN、发票号、金额、日期都相同的收据，会被标出。" },
    { title: "保留原件", body: "原始照片或 PDF 始终可以查看。" },
  ],
  limits: {
    title: "AI 帮忙，由你决定",
    body: "AI 可能读错手写字、褪色的纸或揉皱的收据。字段都可以修改，你确认之前什么都不会入账。Kestly 提供的是估算，不是税务建议，也不会替你向 ATO 申报任何内容。",
  },
  who: [
    { title: "水电工和上门服务", body: "皮卡里堆满了五金店、配件和油费收据。" },
    { title: "承包商和自由职业者", body: "软件、设备和差旅收据，放进同一份清单。" },
    { title: "小店和市集摊主", body: "把一鞋盒的纸质收据，变成可以逐张复核的清单。" },
  ],
  trust: {
    title: "原件始终属于你",
    body: `${STORAGE_SENTENCE_ZH}收据始终可以查看，你确认之前什么都不会入账。数据存放在哪里，见安全与隐私页面。`,
  },
  plan: {
    rows: ["aiReads"],
    note: "开支属于商业套餐；里程记录方案不含收据。",
  },
  faqTitle: "收据扫描常见问题",
  faq: [
    {
      question: "扫描准确吗？",
      answer:
        "清晰的打印收据通常读得准。有字段没读清楚的收据会被标出，请你核对。我们不给出准确率数字。",
    },
    {
      question: "需要 ABN 吗？",
      answer: "需要。开支属于商业套餐；里程记录方案不含收据。",
    },
    {
      question: "它会向 ATO 申报任何内容吗？",
      answer: "不会。Kestly 帮你整理收据；申报由你或你的税务代理完成。",
    },
  ],
  related: [
    { label: "Logbook 法什么时候更划算", href: "/logbook-method" },
    { label: "开发票", href: "/invoicing" },
    { label: "里程记录", href: "/mileage-tracker" },
    { label: "使用方法", href: "/how-it-works" },
    { label: "价格", href: "/pricing" },
    { label: "数据存放在哪里", href: "/trust" },
  ],
  band: {
    title: "别再弄丢收据",
    description: `免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`,
  },
}

const STEP_ART = [
  <ReceiptIllustration key="capture" />,
  <ListMock
    key="review"
    rows={[
      {
        title: ex.merchant2,
        sub: `${ex.total2} · 收据上没有 ABN`,
        badge: "待复核",
        tone: "warning",
      },
      { title: ex.merchant1, sub: `${ex.total1} · GST ${ex.gst1}`, badge: "就绪", tone: "success" },
    ]}
  />,
  <ListMock
    key="confirm"
    rows={[
      {
        title: "工具和设备",
        sub: `GST ${ex.gst1} · 业务用途 100%`,
        badge: "已确认",
        tone: "success",
      },
      {
        title: "办公用品",
        sub: `GST ${ex.gst2} · 业务用途 80%`,
        badge: "已确认",
        tone: "success",
      },
    ]}
  />,
]

/** Icons and pictures are presentation only (same as the English page). */
export const RECEIPT_SCANNER_ZH: ProductPageContent = {
  ...COPY,
  heroCard: <ScanHeroMock />,
  stats: withIcons(COPY.stats, [Percent, Inbox, Percent, ShieldCheck]),
  steps: COPY.steps.map((step, i) => ({ ...step, art: STEP_ART[i], artOnMobile: i > 0 })),
  included: withIcons(COPY.included, [ScanLine, Inbox, Percent, Percent, Copy, Camera]),
  limits: { ...COPY.limits, icon: Sparkles },
  who: withIcons(COPY.who, [Wrench, Briefcase, Store]),
}
